// Mirrors api/src/audit-actions.ts. api/test/catalog-sync.test.ts fails if the
// two drift — the filter dropdown must not offer an action the server rejects,
// and an action the server writes must be filterable here.
export const AUDIT_ACTIONS = [
  'login',
  'login.denied',
  'auth.denied',
  'admin_user.create',
  'admin_user.update',
  'admin_user.delete',
  'admin_user.cognito.read',
  'x5_user.create',
  'x5_user.update',
  'x5_user.identity.read',
  'x5_org.create',
  'x5_org.update',
  'x5_org.members',
  'report.preview',
  'report.run',
  'export.trigger',
  'export.links',
  'export.links.revoke',
  'export.delete',
  'appstream.session.terminate',
  'sms.assignment.create',
  'sms.assignment.update',
  'sms.assignment.delete',
  'sms.number.twoway',
  'sms.number.acquire',
  'cellular.wan.check',
  'gateway.command',
  'gateway.wan.set',
  'cellular.notice.open',
  'cellular.notice.resolve',
  'cellular.notice.decide',
  'cellular.notice.send',
  'cellular.notice.test',
  'gateway.reboot',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export type AuditOutcome = 'requested' | 'success' | 'error' | 'denied';

interface ActionMeta {
  label: string;
  group: string;
}

export const AUDIT_ACTION_META: Record<AuditAction, ActionMeta> = {
  login: { label: 'Signed in', group: 'Access' },
  'login.denied': { label: 'Sign-in refused', group: 'Access' },
  'auth.denied': { label: 'Request denied', group: 'Access' },
  'admin_user.create': { label: 'Admin user added', group: 'Admin users' },
  'admin_user.update': { label: 'Admin user changed', group: 'Admin users' },
  'admin_user.delete': { label: 'Admin user removed', group: 'Admin users' },
  'admin_user.cognito.read': { label: 'Viewed admin sign-in history', group: 'Admin users' },
  'x5_user.create': { label: 'X5 user created', group: 'X5 users' },
  'x5_user.update': { label: 'X5 user changed', group: 'X5 users' },
  'x5_user.identity.read': { label: 'Viewed X5 sign-in history', group: 'X5 users' },
  'x5_org.create': { label: 'Organization created', group: 'X5 organizations' },
  'x5_org.update': { label: 'Organization edited', group: 'X5 organizations' },
  'x5_org.members': { label: 'Organization membership changed', group: 'X5 organizations' },
  'report.preview': { label: 'Report previewed', group: 'Reporting' },
  'report.run': { label: 'Report run + emailed', group: 'Reporting' },
  'export.trigger': { label: 'Export triggered', group: 'Exporter' },
  'export.links': { label: 'Export links issued', group: 'Exporter' },
  'export.links.revoke': { label: 'Export link revoked', group: 'Exporter' },
  'export.delete': { label: 'Export deleted', group: 'Exporter' },
  'appstream.session.terminate': {
    label: 'Streaming session terminated',
    group: 'WorkSpaces Apps',
  },
  'sms.assignment.create': { label: 'SMS number assigned', group: 'SMS numbers' },
  'sms.assignment.update': { label: 'SMS number reassigned', group: 'SMS numbers' },
  'sms.assignment.delete': { label: 'SMS number unassigned', group: 'SMS numbers' },
  'sms.number.twoway': { label: 'Two-way SMS toggled', group: 'SMS numbers' },
  'sms.number.acquire': { label: 'SMS number acquired', group: 'SMS numbers' },
  'cellular.wan.check': { label: 'Router WAN checked', group: 'Cellular' },
  'gateway.command': { label: 'Gateway command sent', group: 'Gateways' },
  'gateway.wan.set': { label: 'Gateway WAN disabled/enabled', group: 'Gateways' },
  'cellular.notice.open': { label: '4G notice case opened', group: 'Cellular' },
  'cellular.notice.resolve': { label: '4G notice case resolved', group: 'Cellular' },
  'cellular.notice.decide': { label: '4G notice decision', group: 'Cellular' },
  'cellular.notice.send': { label: '4G notice emailed to customer', group: 'Cellular' },
  'cellular.notice.test': { label: '4G notice test email', group: 'Cellular' },
  'gateway.reboot': { label: 'Gateway rebooted', group: 'Gateways' },
};

export function isAuditAction(value: string): value is AuditAction {
  return (AUDIT_ACTIONS as readonly string[]).includes(value);
}

/** Actions that move data out of the platform, destroy it, or refuse access. */
export function isSensitive(action: string): boolean {
  return (
    action === 'export.trigger' ||
    action === 'export.links' ||
    action === 'export.delete' ||
    action === 'report.run' ||
    action === 'appstream.session.terminate' ||
    // Not egress, but it changes or removes the number a customer's alarm SMS
    // sends from — and this registry is the only record of that mapping.
    action.startsWith('sms.assignment.') ||
    // Disabling two-way silently drops every inbound reply to that number.
    action === 'sms.number.twoway' ||
    // Commits recurring spend and consumes a finite quota.
    action === 'sms.number.acquire' ||
    // Runs a command on a customer's router.
    action === 'cellular.wan.check' ||
    action === 'gateway.command' ||
    // Takes a site's wired internet out of service, or puts it back.
    action === 'gateway.wan.set' ||
    // Takes the site's internet and its X5 tunnel down for minutes.
    action === 'gateway.reboot' ||
    // Emails a customer, or authorizes the nightly run to.
    action === 'cellular.notice.send' ||
    action === 'cellular.notice.decide' ||
    action.endsWith('.denied')
  );
}
